/**
 * WindowChrome — the faux-app title bar shared by the landing's "dashboard
 * window" surfaces: three traffic-light dots on the left, an optional row of
 * source TABS in the middle, and an optional trailing slot (e.g. a copy button)
 * on the right. Extracted so any window-styled panel reuses the same chrome.
 *
 * Brutalist rules: zero border-radius EXCEPT the three traffic-light dots, which
 * are the ONLY `rounded-full` elements allowed. Preflight is OFF, so every border
 * here is declared `border-solid`.
 */
import * as React from 'react';

export interface WindowTab {
  key: string;
  label: string;
}

export interface WindowChromeProps {
  /** Tabs rendered in the chrome. Omit for a chrome with no tabs. */
  tabs?: WindowTab[];
  /** Currently active tab key. */
  activeKey?: string;
  /** Tab-change handler. */
  onSelect?: (key: string) => void;
  /** Accessible name for the tablist. */
  tablistLabel?: string;
  /** Trailing-right slot (e.g. a copy-icon button). */
  trailing?: React.ReactNode;
  /** Border class for the bottom rule (defaults to a light cream rule). */
  borderClassName?: string;
}

/** The three macOS-style traffic-light dots — the only rounded elements here. */
const TrafficLights: React.FC = () => (
  <div className="flex shrink-0 gap-2" aria-hidden>
    <span className="h-3 w-3 rounded-full bg-[#ff5f56]" />
    <span className="h-3 w-3 rounded-full bg-[#ffbd2e]" />
    <span className="h-3 w-3 rounded-full bg-[#27c93f]" />
  </div>
);

/**
 * Title bar: traffic lights + (optional) tab chips + (optional) trailing slot.
 * The active tab reads as a filled chip; the rest are muted, borderless.
 */
const WindowChrome: React.FC<WindowChromeProps> = ({
  tabs,
  activeKey,
  onSelect,
  tablistLabel = 'View',
  trailing,
  borderClassName = 'border-white/20',
}) => (
  <div
    className={[
      'flex items-center gap-3 overflow-x-auto border-b-2 border-solid px-4 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
      borderClassName,
    ].join(' ')}
  >
    <TrafficLights />

    {tabs && tabs.length > 0 && (
      <div role="tablist" aria-label={tablistLabel} className="flex shrink-0 items-center gap-1">
        {tabs.map((t) => {
          const selected = t.key === activeKey;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onSelect?.(t.key)}
              className={[
                'border border-solid px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.14em] transition-colors sm:text-xs',
                selected
                  ? 'border-[#3FB8C8] bg-[#0a2e34] text-[#7FE0EC]'
                  : 'border-transparent text-white/45 hover:text-white/75',
              ].join(' ')}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    )}

    {trailing && <div className="ml-auto flex shrink-0 items-center">{trailing}</div>}
  </div>
);

export default WindowChrome;
